import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { getToken } from "../../../../helpers/Jwt";

const EntryContact = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const initialValues = {
        display_name: '',
        type: {
            customer: false,
            supplier: false,
            employee: false,
            others: false,
        },
        accountMapping: {
            receivable_account: '',
            payable_account: '',            
            primary_payment_terms: '',
        },
    };

    const typeContact = [
        { label: 'Pelanggan', name: 'customer' },
        { label: 'Supplier', name: 'supplier' },
        { label: 'Karyawan', name: 'employee' },
        { label: 'Lainnya', name: 'others' },
    ];

    const validationSchema = Yup.object().shape({
        display_name: Yup.string().required('Nama Tampilan Kontak wajib diisi.')
    });

    const [accountPayable, setAccountPayable] = useState([]);
    const [accountReceivable, setAccountReceivable] = useState([]);
    const [termsPayment, setTermsPayment] = useState([]);

    const [isAccountMappingVisible, setIsAccountMappingVisible] = useState(false);

    const toggleAccountMappingVisibility = () => {
        setIsAccountMappingVisible(!isAccountMappingVisible);
    };

    const [error, setError] = useState(null);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const PATH_CONTACT = "api/erpppsu/contact";
    const PATH_EXPENSE_PRTY_PAYABLE = "api/erpppsu/account/expense?priority=payable";
    const PATH_EXPENSE_PRTY_RECEIVABLE = "api/erpppsu/account/expense?priority=receivable";
    const PATH_TERMS_PAYMENT = "api/erpppsu/account/termspayment";
    
    // Fetch data from the API
    const fetchData = async (URL) => {
        try {
            const response = await axios.get(URL);
            return response.data; // Return only the data from the response
        } catch (err) {
            setError("Failed to fetch data. Please try again later.");
            console.log(err);
        }
    };

    useEffect(() => {
        fetchData(`${BASE_URL}${PATH_EXPENSE_PRTY_PAYABLE}`,).then((data) => {
            setAccountPayable(data);
        });

        fetchData(`${BASE_URL}${PATH_EXPENSE_PRTY_RECEIVABLE}`,).then((data) => {
            setAccountReceivable(data);
        });      
        
        fetchData(`${BASE_URL}${PATH_TERMS_PAYMENT}`,).then((data) => {
            setTermsPayment(data);
        });      
    }, []);

    const onSubmit = async (values, requests) => {
        try {
            const payload = {
                id,
                ...values,
            };

            await axios.post(`${BASE_URL}${PATH_CONTACT}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                }
            });

            navigate('/activity-erpppsu/contact');
        } catch (error) {
            alert(error);
            console.error('Error submitting form:', error);
        } finally {
            requests.setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto p-4">
            <h2 className="text-2xl font-bold mb-4">
                {id ? `Edit Kontak` : 'Tambah Kontak'}
            </h2>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({ values, handleChange, setFieldValue, isSubmitting }) => (
                    <Form>
                        {/* Display Name Field */}
                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="display_name">Nama Tampilan</label>
                            <Field
                                className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                name="display_name"
                            />
                            <ErrorMessage name="display_name" component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="type">Tipe Kontak</label>
                            <div className="p-3" style={{ display: 'flex', gap: '20px' }}>
                                {typeContact.map(({ label, name }) => (
                                    <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <input
                                            type="checkbox"
                                            name={name}
                                            checked={values.type[name]}
                                            onChange={(e) => setFieldValue(`type.${name}`, e.target.checked)}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Expandable Account Mapping Section */}
                        <div className="px-4 py-2">
                            <button
                                type="button"
                                onClick={toggleAccountMappingVisibility}
                                className="flex items-center gap-2 text-blue-500 font-bold focus:outline-none"
                            >
                                <span>{isAccountMappingVisible ? '▾' : '▸'}</span> Pemetaan Akun
                            </button>

                            {isAccountMappingVisible && (
                                <div className="mt-4 border-t pt-4">
                                    {/* Receivable Account */}
                                    <div className="gap-4 mb-4">
                                        <label htmlFor="receivable_account">Akun Piutang</label>
                                        <Field
                                            as="select"
                                            name="accountMapping.receivable_account"
                                            className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                            onChange={(e) => setFieldValue(`accountMapping.receivable_account`, e.target.value)}
                                        >
                                            <option value="">Pilih Akun Piutang</option>
                                            {accountReceivable.map((account) => (
                                                <option key={account.id} value={account.id}>
                                                    {account.name}
                                                </option>
                                            ))}
                                            {/* Add more options as needed */}
                                        </Field>
                                    </div>

                                    {/* Payable Account */}
                                    <div className="gap-4 mb-4">
                                        <label htmlFor="payable_account">Akun Hutang</label>
                                        <Field
                                            as="select"
                                            name="accountMapping.payable_account"
                                            className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                            onChange={(e) => setFieldValue(`accountMapping.payable_account`, e.target.value)}
                                        >
                                            <option value="">Pilih Akun Hutang</option>
                                            {accountPayable.map((account) => (
                                                <option key={account.id} value={account.id}>
                                                    {account.name}
                                                </option>
                                            ))}
                                        </Field>
                                    </div>

                                    {/* Primary Payment Terms */}
                                    <div className="gap-4 mb-4">
                                        <label htmlFor="primary_payment_terms">Syarat Pembayaran Utama</label>
                                        <Field
                                            as="select"
                                            name="accountMapping.primary_payment_terms"
                                            className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                            onChange={(e) => setFieldValue(`accountMapping.primary_payment_terms`, e.target.value)}
                                        >
                                            <option value="">Pilih Pembayaran</option>
                                            {termsPayment.map((account) => (
                                                <option key={account.id} value={account.id}>
                                                    {account.name}
                                                </option>
                                            ))}
                                        </Field>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="m-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring focus:ring-blue-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EntryContact;
